import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Activity, BellRing, Building2, CircleHelp, ClipboardCheck, FileKey2, Grid2X2, KeyRound, LayoutDashboard, ListChecks, LogOut, Menu, Radio, ReceiptText, Search, ServerCog, Settings2, ShieldCheck, UserCog, Workflow, X } from 'lucide-react'
import { api, clearCredentials } from '../lib/api'
import { can } from '../lib/permissions'
import type { GlobalSearchItem, LicensePermission, Session, SigningServiceHealth, UserRole } from '../types'

type NavigationItem = { to: string; label: string; icon: typeof LayoutDashboard; permission: LicensePermission; group: string; roles?: UserRole[] }

const nav: NavigationItem[] = [
  { to: '/tasks', label: '个人任务箱', icon: ClipboardCheck, permission: 'DASHBOARD_VIEW', group: '开始工作' },
  { to: '/deliveries', label: '交付单', icon: Workflow, permission: 'LICENSE_VIEW', group: '开始工作' },
  { to: '/', label: '业务概览', icon: LayoutDashboard, permission: 'DASHBOARD_VIEW', group: '开始工作' },
  { to: '/customers', label: '客户与联系人', icon: Building2, permission: 'CUSTOMER_VIEW', group: '业务资料' },
  { to: '/commercial-authorizations', label: '商业授权', icon: ReceiptText, permission: 'COMMERCIAL_AUTHORIZATION_VIEW', group: '业务资料' },
  { to: '/applications', label: '开通申请', icon: ListChecks, permission: 'LICENSE_VIEW', group: '业务资料' },
  { to: '/installations', label: '交付与安装', icon: ServerCog, permission: 'INSTALLATION_VIEW', group: '交付查询', roles: ['SUPER_ADMIN', 'LICENSE_OPERATOR', 'LICENSE_ISSUER', 'APPROVER'] },
  { to: '/activations', label: '激活与交付回执', icon: Radio, permission: 'ACTIVATION_VIEW', group: '交付查询', roles: ['LICENSE_OPERATOR'] },
  { to: '/operations', label: '客户服务与交付', icon: BellRing, permission: 'CUSTOMER_PORTAL_MANAGE', group: '交付查询', roles: ['LICENSE_OPERATOR'] },
  { to: '/licenses', label: '已签发 License', icon: FileKey2, permission: 'LICENSE_VIEW', group: '交付查询' },
  { to: '/modules', label: '产品套餐', icon: Grid2X2, permission: 'PRODUCT_CATALOG_VIEW', group: '治理配置' },
  { to: '/users', label: '用户与岗位', icon: UserCog, permission: 'USER_MANAGE', group: '治理配置' },
  { to: '/protocol', label: '安全与密钥治理', icon: ShieldCheck, permission: 'PROTOCOL_VIEW', group: '治理配置', roles: ['SUPER_ADMIN', 'LICENSE_OPERATOR', 'LICENSE_ISSUER'] },
]

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: '平台超级管理员', USER_ADMIN: '平台管理员', APPROVER: 'License 审批人',
  LICENSE_ISSUER: 'License 签发人', LICENSE_OPERATOR: 'License 申请人',
}

const titles: Record<string, { title: string; eyebrow: string }> = {
  '/': { title: '业务概览', eyebrow: '查看经营数据、风险摘要和业务提醒' },
  '/tasks': { title: '个人任务箱', eyebrow: '唯一待办入口，按责任人汇总全部权威任务' },
  '/deliveries': { title: '交付单', eyebrow: '从一条业务链跟踪授权开通、安装和激活' },
  '/customers': { title: '客户与联系人', eyebrow: '建立客户档案并维护交付联系人' },
  '/commercial-authorizations': { title: '商业授权', eyebrow: '以复核通过的合同或订单作为购买权益依据' },
  '/licenses': { title: '已签发授权文件', eyebrow: '查询已生成签名授权文件及其权益状态' },
  '/applications': { title: '开通申请', eyebrow: '跟踪提交、审批与待签发流程' },
  '/applications/new': { title: '新建开通申请', eyebrow: '选择复核通过的商业授权快照' },
  '/licenses/new': { title: '新建开通申请', eyebrow: '选择复核通过的商业授权快照' },
  '/approvals': { title: '待我审批', eyebrow: '完成审批和高风险复核' },
  '/issuance': { title: '签发与授权文件', eyebrow: '为已审批的开通申请签发授权文件，并查看签名文件版本' },
  '/activations': { title: '激活与交付回执', eyebrow: '跟进客户设备的离线交付与回执确认' },
  '/protocol': { title: '安全与密钥治理', eyebrow: '管理签名密钥、信任包和撤销清单' },
  '/operations': { title: '客户服务与交付', eyebrow: '处理离线激活、续费扩容和业务通知' },
  '/installations': { title: '交付与安装', eyebrow: '管理产品部署、激活与迁移记录' },
  '/modules': { title: '产品套餐', eyebrow: '查看三档正式产品版本及其授权能力' },
  '/users': { title: '用户与岗位', eyebrow: '维护正式岗位、登录权限和离岗交接' },
}

const searchTypeLabels: Record<string, string> = { CUSTOMER: '客户', COMMERCIAL_AUTHORIZATION: '商业授权', LICENSE: '授权文件', INSTALLATION: '安装实例', SERVICE_REQUEST: '服务工单' }
const keyLifecycleLabels: Record<string, string> = {
  NORMAL: '周期正常', DUE_SOON: '临近轮换', OVERDUE: '已超建议周期', UNKNOWN: '周期待确认',
}

function shortDate(value?: string) {
  return value ? new Date(value).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-'
}

function GlobalSearch() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<GlobalSearchItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); setOpen(true)
      }
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
  useEffect(() => { if (open) window.setTimeout(() => input.current?.focus(), 0) }, [open])
  useEffect(() => {
    if (!open || query.trim().length < 2) { setItems([]); setError(''); return }
    const timer = window.setTimeout(() => {
      setLoading(true); setError('')
      api.globalSearch(query).then(result => setItems(result.items)).catch(value => setError(value instanceof Error ? value.message : '搜索失败')).finally(() => setLoading(false))
    }, 180)
    return () => window.clearTimeout(timer)
  }, [open, query])
  function choose(item: GlobalSearchItem) { setOpen(false); setQuery(''); navigate(item.destination) }

  return <><button className="global-search-trigger" onClick={() => setOpen(true)} aria-label="全局搜索"><Search size={16} /><span>搜索客户、合同、授权文件或工单</span><kbd>⌘K</kbd></button>
    {open && <div className="global-search-layer" role="presentation" onMouseDown={() => setOpen(false)}><section className="global-search-dialog" role="dialog" aria-modal="true" aria-label="全局搜索" onMouseDown={event => event.stopPropagation()}><div className="global-search-input"><Search size={19} /><input ref={input} value={query} onChange={event => setQuery(event.target.value)} placeholder="至少输入 2 个字符，例如客户名、合同号或授权文件编号" /><button onClick={() => setOpen(false)} aria-label="关闭搜索"><X size={18} /></button></div><div className="global-search-results">{query.trim().length < 2 ? <p>输入至少 2 个字符开始搜索。仅显示当前岗位有权查看的记录。</p> : loading ? <p>正在搜索...</p> : error ? <p className="global-search-error">{error}</p> : items.length ? items.map((item, index) => <button key={`${item.type}-${item.title}-${index}`} onClick={() => choose(item)}><small>{searchTypeLabels[item.type] || item.type}</small><b>{item.title}</b><span>{item.detail}</span></button>) : <p>没有找到匹配记录，或当前岗位没有该记录的查看权限。</p>}</div></section></div>}</>
}

export function AppShell() {
  const [open, setOpen] = useState(false)
  const [session, setSession] = useState<Session>({ authenticated: false })
  const [signingHealth, setSigningHealth] = useState<SigningServiceHealth>()
  const [loggingOut, setLoggingOut] = useState(false)
  const [logoutError, setLogoutError] = useState('')
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const heading = location.pathname === '/applications' && session.role === 'SUPER_ADMIN'
    ? { title: '申请流程查询', eyebrow: '只读监督全部开通申请，不参与申请、审批或签发' }
    : location.pathname.startsWith('/license-cases/')
    ? { title: '交付单', eyebrow: '用一条业务链跟踪开通申请、License 与交付状态' }
    : location.pathname.startsWith('/tasks/')
      ? { title: '责任任务', eyebrow: '核对责任、证据与当前业务阶段' }
      : location.pathname.startsWith('/customer-cases/')
      ? { title: '客户开通与服务单', eyebrow: '从商业授权到服务请求的全客户生命周期视图' }
    : titles[location.pathname] || titles['/']
  const visibleNavigation = nav.filter(item => can(session, item.permission)
    && (!item.roles || (session.role && item.roles.includes(session.role as UserRole))))
  const navigationGroups = [...new Set(visibleNavigation.map(item => item.group))]
  const canViewSigningHealth = can(session, 'LICENSE_ISSUE')
    || can(session, 'SIGNING_KEY_ROTATE')
    || can(session, 'SIGNING_KEY_ROTATION_APPROVE')

  useEffect(() => {
    api.session().then(setSession).catch(() => undefined)
  }, [])
  useEffect(() => {
    if (!session.authenticated || !canViewSigningHealth) {
      setSigningHealth(undefined)
      return
    }
    const refreshSigningHealth = () => api.signingServiceHealth().then(setSigningHealth).catch(() => setSigningHealth(undefined))
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') refreshSigningHealth() }
    refreshSigningHealth()
    const interval = window.setInterval(refreshSigningHealth, 60_000)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => { window.clearInterval(interval); document.removeEventListener('visibilitychange', refreshWhenVisible) }
  }, [session.authenticated, canViewSigningHealth])

  async function logout() {
    if (loggingOut) return
    setLoggingOut(true); setLogoutError('')
    try {
      await api.logout()
      clearCredentials()
      setSession({ authenticated: false })
      setOpen(false)
      navigate('/login', { replace: true })
    } catch (value) {
      setLogoutError(value instanceof Error ? value.message : '退出登录失败，请重试')
    } finally {
      setLoggingOut(false)
    }
  }

  return <>
  <div className="app-shell">
    <aside className={`sidebar ${open ? 'sidebar--open' : ''}`}>
      <div className="brand"><span className="brand-mark"><ShieldCheck size={21} /></span><div><b>License Center</b><small>数巅科技</small></div></div>
      <button className="icon-button sidebar-close" onClick={() => setOpen(false)} aria-label="关闭菜单"><X size={20} /></button>
      <nav>{navigationGroups.map(group => <div className="nav-group" key={group}><small>{group}</small>{visibleNavigation.filter(item => item.group === group).map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={() => setOpen(false)}><item.icon size={19} /><span>{session.role === 'SUPER_ADMIN' && item.to === '/applications' ? '申请流程查询' : item.label}</span></NavLink>)}</div>)}</nav>
      <div className="sidebar-foot">{canViewSigningHealth && <div className={`system-state ${signingHealth?.status === 'DEGRADED' ? 'system-state--degraded' : ''} ${signingHealth?.status === 'READY' ? `system-state--${(signingHealth.rotationStatus || 'UNKNOWN').toLowerCase().replace('_', '-')}` : ''}`}><Activity size={15} /><span><b>{signingHealth?.label || '签发状态正在确认'}{signingHealth?.status === 'READY' && <em>{keyLifecycleLabels[signingHealth.rotationStatus || 'UNKNOWN'] || signingHealth.rotationStatus}</em>}</b>{signingHealth?.status === 'READY' ? <><small className="system-state-key">当前密钥：{signingHealth.activeKid || '已加载'}</small>{signingHealth.activeKidActivatedAt && <small>激活 {shortDate(signingHealth.activeKidActivatedAt)} · 已使用 {signingHealth.activeKeyAgeDays ?? 0} 天</small>}{signingHealth.recommendedRotationAt && <small>建议 {shortDate(signingHealth.recommendedRotationAt)} 前轮换</small>}<small>历史验签公钥：{signingHealth.retainedVerificationKeyCount ?? 0} 把</small>{signingHealth.clusterStatus === 'OUT_OF_SYNC' && <small className="system-state-warning">集群：{signingHealth.outOfSyncInstanceCount ?? 0} 台待切换</small>}{signingHealth.clusterStatus === 'INSTANCE_ID_COLLISION' && <small className="system-state-warning">集群：实例标识冲突，已阻断签发</small>}{signingHealth.clusterStatus === 'HEARTBEAT_MISSING' && <small className="system-state-warning">集群：心跳缺失，已阻断签发</small>}{signingHealth.clusterStatus === 'CONSISTENT' && <small>集群：{signingHealth.synchronizedInstanceCount ?? 0} 台已同步</small>}</> : <small>状态由平台运行检查提供，不展示密钥材料</small>}{can(session, 'SIGNING_KEY_ROTATION_APPROVE') && !can(session, 'SIGNING_KEY_ROTATE') ? <Link to="/tasks">查看我的密钥审批</Link> : can(session, 'PROTOCOL_VIEW') && <Link to="/protocol">进入安全与密钥治理</Link>}</span></div>}{logoutError && <p className="sidebar-error" role="alert">{logoutError}</p>}<button onClick={() => void logout()} disabled={loggingOut}><LogOut size={17} />{loggingOut ? '正在退出...' : '退出登录'}</button></div>
    </aside>
    {open && <div className="sidebar-backdrop" onClick={() => setOpen(false)} />}
    <main className="main-area">
      <header className="topbar"><button className="icon-button menu-button" onClick={() => setOpen(true)} aria-label="打开菜单"><Menu /></button><div><h1>{heading.title}</h1><span>{heading.eyebrow}</span></div><GlobalSearch /><a className="topbar-help" href="/help.html" target="_blank" rel="noreferrer" aria-label="打开使用帮助"><CircleHelp size={16} /><span>使用帮助</span></a><div className="operator"><span>{roleLabels[session.role || ''] || '内部用户'} · {session.username || ''}</span><button className="operator-account" type="button" onClick={() => setAccountSettingsOpen(true)} aria-label="打开账号设置"><Settings2 size={15} /><span>账号设置</span></button></div></header>
      <section className="page-content"><Outlet /></section>
    </main>
  </div>
  {accountSettingsOpen && <AccountSettingsModal username={session.username || ''} onClose={() => setAccountSettingsOpen(false)} />}
  </>
}

function AccountSettingsModal({ username, onClose }: { username: string, onClose: () => void }) {
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (newPassword !== confirmation) { setError('两次输入的新密码不一致'); return }
    setSaving(true); setError('')
    try {
      await api.changeOwnPassword(currentPassword, newPassword)
      await api.logout().catch(() => undefined)
      clearCredentials()
      navigate('/login?passwordChanged=1', { replace: true })
    } catch (value) {
      setError(value instanceof Error ? value.message : '密码修改失败，请稍后重试')
    } finally { setSaving(false) }
  }

  return <div className="modal-layer"><section className="modal compact-modal account-settings-modal" role="dialog" aria-modal="true" aria-labelledby="account-settings-title"><div className="modal-heading"><div><small>ACCOUNT SECURITY</small><h3 id="account-settings-title">账号设置</h3></div><button className="icon-button" type="button" onClick={onClose} aria-label="关闭账号设置"><X size={19} /></button></div><form onSubmit={submit}><div className="modal-context"><KeyRound size={18} /><div><b>{username || '当前账号'}</b><small>修改后所有已登录设备都会退出，需要使用新密码重新登录。</small></div></div><label>当前密码<input type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} autoFocus autoComplete="current-password" required /></label><label>新密码<input type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} autoComplete="new-password" minLength={8} maxLength={72} required /><small>至少 8 位，包含大写字母、小写字母、数字和特殊字符。</small></label><label>确认新密码<input type="password" value={confirmation} onChange={event => setConfirmation(event.target.value)} autoComplete="new-password" minLength={8} maxLength={72} required /></label>{error && <div className="form-error">{error}</div>}<div className="modal-actions"><button type="button" className="button button--ghost" onClick={onClose} disabled={saving}>取消</button><button type="submit" className="button button--primary" disabled={saving}>{saving ? '正在更新...' : '确认修改'}</button></div></form></section></div>
}
